import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTranslation } from 'react-i18next';
import { deckKeys, reviewQueueKey } from '@/entities/deck';
import { deleteCard } from '@/shared/api/decks';
import { Button, ErrorMessage } from '@/shared/ui';

/** Deletes one card after confirmation; its review history is kept. */
export function DeleteCardButton({
  deckId,
  cardId,
  front,
}: {
  deckId: string;
  cardId: string;
  front: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => deleteCard(cardId),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: deckKeys.one(deckId) }),
        queryClient.invalidateQueries({ queryKey: deckKeys.all, exact: true }),
        queryClient.invalidateQueries({ queryKey: reviewQueueKey }),
      ]),
  });

  return (
    <>
      <Button
        variant="danger"
        aria-label={t('decks.deleteCard', { front })}
        disabled={mutation.isPending}
        onClick={() => {
          if (window.confirm(t('decks.deleteCardConfirm'))) {
            mutation.mutate();
          }
        }}
      >
        {t('common.delete')}
      </Button>
      {mutation.isError && <ErrorMessage error={mutation.error} />}
    </>
  );
}
